/* Supply › Reports › Overview — the numbers behind the dashboard.
 *
 * One call:
 *
 *   GET v1/reports/overview?warehouseId&fromDate&toDate
 *
 * The server does the aggregation (see OVERVIEW_API_SPEC.md). This module exists
 * only to map its envelope onto the shape the screen consumes, so the screen
 * never reads a backend field name directly and a rename lands in one place.
 *
 * This replaced a client-side aggregation that paged three list endpoints. That
 * version could not cost a batch which had sold out — the inventory endpoint only
 * returns rows with stock left — so its margin covered a fraction of sold lines
 * and the screen had to say so. The server joins the sold line straight to its
 * lot, so the same figure comes back fully costed.
 */
import { httpSupplier } from "../../HttpCommonALL";
import { getCurrentUserName } from "../../../Services/warehouseContext";

const xUser = () => ({ "X-User": getCurrentUserName() });
const num = (v) => Number(v) || 0;

const unwrap = (r) => {
  const env = r?.data;
  if (env && typeof env === "object" && "success" in env) {
    if (env.success === false) throw new Error(env.message || "Request failed");
    return env.data;
  }
  return env;
};

/* The deployed response and OVERVIEW_API_SPEC.md disagree on several field names
   (`inventoryValue.atMrp` vs `inventory.mrpValue`, `daily` vs `series`, …). Both
   spellings are accepted so the screen keeps working whichever one an environment
   serves; the live names are listed first. */
const fromResponse = (d = {}) => {
  const inv = d.inventoryValue || d.inventory || {};
  const sales = d.sales || {};
  const purchase = d.purchase || {};
  const profit = d.profit || {};
  const daily = Array.isArray(d.daily) ? d.daily : Array.isArray(d.series) ? d.series : [];

  /* Coverage is 0–1: the share of sold lines the server could cost. It should be
     1 — the screen calls it out when it isn't, because a margin computed over
     some of the lines is not the store's margin. */
  const costedPct = profit.costedLinePercentage ?? profit.coveragePct;
  const totalLines = num(profit.totalLines);
  const marginCoverage =
    costedPct != null
      ? num(costedPct) / 100
      : totalLines
      ? num(profit.costedLines) / totalLines
      : 1;

  return {
    valuation: {
      mrpValue: num(inv.atMrp ?? inv.mrpValue),
      ptrValue: num(inv.atPtr ?? inv.ptrValue),
      skuCount: num(inv.skuCount),
      lotCount: num(inv.batchCount),
    },
    trade: {
      /* Zero-filled server-side: every day in the range is present, so a day with
         no trade plots as zero instead of closing the gap. */
      series: daily.map((r) => ({
        date: String(r.date || "").slice(0, 10),
        sales: num(r.sales),
        purchase: num(r.purchase),
        profit: num(r.profit),
      })),
      salesAmount: num(sales.amount),
      salesCount: num(sales.invoiceCount ?? sales.count),
      purchaseAmount: num(purchase.amount),
      purchaseCount: num(purchase.invoiceCount ?? purchase.count),
      /* Gross margin (revenue ex-GST − cost of sales), NOT sales − purchase: a
         week spent stocking up would otherwise read as a loss. */
      profit: num(profit.grossProfit ?? profit.grossMargin),
      marginPct: num(profit.marginPercentage ?? profit.marginPct),
      marginCoverage,
    },
    warehouseName: d.warehouseName || "",
  };
};

/* Payment modes come back as the backend enum. These are the same labels the
   printed invoice uses, extended to the rest of the PaymentMode values; anything
   unmapped falls back to "NET_BANKING" -> "Net banking" so a new mode added
   server-side still reads as words rather than an enum. */
const PAYMENT_MODE_LABELS = {
  CASH: "Cash",
  CARD: "Card",
  UPI: "UPI",
  NET_BANKING: "Net banking",
  WALLET: "Wallet",
  CHEQUE: "Cheque",
  CREDIT: "Credit",
  E_CLINIC_ONLINE: "E-Clinic (online)",
  B2C_ONLINE: "B2C (online)",
  COUNTER: "Counter sale",
  OTHER: "Other",
};

const paymentModeLabel = (mode) => {
  const key = String(mode || "").trim().toUpperCase().replace(/[\s-]+/g, "_");
  if (!key) return "Unspecified";
  if (PAYMENT_MODE_LABELS[key]) return PAYMENT_MODE_LABELS[key];
  const words = key.replace(/_/g, " ").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/* The payment breakdown is capped server-side at a much shorter window than the
   overview itself. Checked before the call so an over-long range is a message on
   the panel instead of a 400. */
export const PAYMENT_MAX_DAYS = 45;

const overviewService = {
  /* One call for the screen. `warehouseId` is required — stock value is held per
     store, and the API rejects the call without it. */
  async load({ warehouseId, fromDate, toDate }) {
    const r = await httpSupplier.get("v1/reports/overview", {
      params: { warehouseId, fromDate, toDate },
      headers: xUser(),
    });
    return fromResponse(unwrap(r) || {});
  },

  /* GET v1/reports/sales-by-payment -> how the window's billed sales split across
     payment modes. Rows arrive sorted by share; sorted again here so the chart
     cannot depend on that.

     NOT deployed everywhere yet — stage 404s on it at the time of writing. The
     caller treats a 404 as "this environment has no breakdown" and hides the
     panel rather than failing the screen, so this stays a plain rejection. */
  async salesByPayment({ warehouseId, fromDate, toDate }) {
    const r = await httpSupplier.get("v1/reports/sales-by-payment", {
      params: { warehouseId, fromDate, toDate },
      headers: xUser(),
    });
    const d = unwrap(r) || {};
    const rows = Array.isArray(d.breakdown) ? d.breakdown : [];
    return {
      total: {
        amount: num(d.total?.amount),
        invoiceCount: num(d.total?.invoiceCount),
      },
      rows: rows
        .map((b) => ({
          mode: String(b.paymentMode || ""),
          label: paymentModeLabel(b.paymentMode),
          amount: num(b.amount),
          invoiceCount: num(b.invoiceCount ?? b.count),
          sharePct: num(b.sharePercentage ?? b.sharePct),
        }))
        .sort((a, b) => b.amount - a.amount),
    };
  },
};

export default overviewService;
